'use client'

import { IconDropdownNextSmall } from 'icons/components/IconDropdownNextSmall'
import { IconDropdownPrevSmall } from 'icons/components/IconDropdownPrevSmall'
import type { RefObject } from 'react'
import { useResponsive } from 'responsive/hooks/useResponsive/useResponsive'
import { cx } from 'utils/cx'

import { ButtonIconTransparent } from '@fukku/button/Icon/Transparent'
import {
	CommBanner,
	type CommBannerProps as CommBannerType,
} from '../CommBanner'
import { useHorizontalScroll } from '../HorizontalScroll/hooks'

import styles from './CommsBanner.module.scss'

export interface CommsBannerProps {
	readonly id: string
	readonly comms: Omit<CommBannerType, 'commRef'>[]
	readonly previousButtonLabel: string
	readonly nextButtonLabel: string
	readonly className?: string
}

export function CommsBanner(props: CommsBannerProps) {
	const {
		comms,
		id,
		className = '',
		previousButtonLabel,
		nextButtonLabel,
	} = props

	const { isLargeOrGreater } = useResponsive()

	const {
		horizontalScrollRef,
		horizontalScrollEvents,
		handleLeftScroll,
		handleRightScroll,
		isAtStart,
		isAtEnd,
	} = useHorizontalScroll()

	if (comms.length === 1) {
		return <CommBanner {...comms[0]} key={comms[0].id} />
	}

	return (
		<div className={styles.wrapper}>
			{isLargeOrGreater && (
				<ButtonIconTransparent
					className={styles.arrow}
					data-testid={`commsBanner.${id}.previous.button`}
					disabled={isAtStart}
					onClick={handleLeftScroll}
					aria-label={previousButtonLabel}
				>
					<IconDropdownPrevSmall />
				</ButtonIconTransparent>
			)}
			<ul
				{...horizontalScrollEvents}
				ref={horizontalScrollRef as RefObject<HTMLUListElement>}
				className={cx(styles.comms, className)}
				data-testid={`commsBanner.${id}.container`}
				id={id}
			>
				{comms.map(
					({
						id: commId,
						className: commClassName,
						title,
						subtitle,
						links,
					}) => (
						<li key={commId} className={styles.banner}>
							<CommBanner
								id={commId}
								className={cx(styles.comm, commClassName)}
								title={title}
								subtitle={subtitle}
								links={links}
							/>
						</li>
					)
				)}
			</ul>
			{isLargeOrGreater && (
				<ButtonIconTransparent
					data-testid={`commsBanner.${id}.next.button`}
					className={styles.arrow}
					onClick={handleRightScroll}
					aria-label={nextButtonLabel}
					disabled={isAtEnd}
				>
					<IconDropdownNextSmall />
				</ButtonIconTransparent>
			)}
		</div>
	)
}

export default CommsBanner
